import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import * as fromRoot from '../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '../../../core/projects.service';
import { PROJECT_DETAILS_SCREEN_INCLUDE } from '../../../pages/projects-details-page/screen/project-details-screen.component';
import { PunchlistItemDetailComponent } from '../../../pages/projects-details-page/components/tab-screens/punchlist/punchlist-item-detail.component';
import * as moment from 'moment';
import { FetchAllBidItemsRequest, FetchIssueTypesRequest, FetchProjectDetailsRequest, FetchSiteWiseDailyReportSummaryRequest, FetchWarrantyClaimsRequest, FetchWarrantiesRequest, FetchWarrantiesSuccess } from '../../../core/projects.actions';
import { mapNeedsProjectDetails, mapProjectDetailsPayload } from '../map-project-details';
import { allBidItems, projectDetails, siteWiseDailyReportsSummary, warrantyClaimsInfo, warrantiesInfo } from '../../../core/projects.selectors';
import { MapService } from '../../../../../shared/services/map.service';
import { DailyReportService } from '../../../../../shared/services/daily-report.service';
import { DailyReportCacheService } from '../../../../../shared/services/daily-report-cache.service';
import { BidService } from '../../../../../shared/services/bid.service';
import { AppHttpService } from '../../../../../shared/services/app-http.service';
import { MAP_VIEW_BY_COLORS, getLayerDefaults } from '../map.constants';
import { LabelByOption, MapLabelConfig } from '../../../../../shared/types/map.types';
import { StationRangeMapGeometryService } from '../../../../../shared/services/station-range-map-geometry.service';
import { MapSymbolFactory } from '../../../../../shared/services/map-symbol-factory.service';
import { computeOutlierReferences, rejectOutlierLocation, GeoPoint } from '../../../../../shared/helpers/geo-outlier';
import { LINKED_ONLY_FILTER } from '../../../../../shared/helpers/design-context';
import { projectForRoute } from '../../../../../shared/helpers/project-widget-config';
import {
  ISSUES_TAB,
  issueMapMarkers,
  issuesMapQuery,
  issuesTabStateFromParams,
} from '../../../../../shared/helpers/issues-tab';
import { IssueService } from '../../../../issue/issue.service';
import {
  punchlistItemFieldMap,
  PunchlistItemFieldName,
  resolvePunchlistItemFields,
} from '../../../../../shared/helpers/project-punchlist-item-config';

/** The punch list item fields the marker popup reads (app-claim-map-popup). */
const PUNCHLIST_POPUP_FIELDS: PunchlistItemFieldName[] = ['description', 'severity', 'need_by_date'];

export interface Inspector {
  id: string;
  name: string;
  email?: string;
  color?: string;
}

@Component({
    selector: 'app-map-layer-container',
    templateUrl: './map-layer-container.component.html',
    styleUrls: ['./map-layer-container.component.scss'],
    standalone: false
})
export class MapLayerContainerComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true, read: ElementRef }) mapContainer: ElementRef;

  projectId: string;
  isFullScreen = false; // Controls sidenav visibility
  fullscreenElement: HTMLElement;

  // Map layer state
  viewMode: 'sites' | 'bid-item-sites' | 'bid-item-day' | 'pictures' | 'geo-details' = 'sites';
  searchQuery = '';
  dateRange = {
    start: null,
    end: null,
  };

  // Data for filtering
  private sites: any[] = [];
  private allProjectSites: any[] = [];
  filteredSites: any[] = [];
  private siteIdsToFilter: string[] = [];
  private statusIdsToFilter: string[] = [];
  private creatorIdsToFilter: string[] = [];
  private reportGeometries: any[] = [];
  private cachedReports: any[] = [];
  private currentSitesWithGeometries: string[] = []; // Track sites that currently have geometries
  private siteColorMap: { [siteId: string]: string } = {}; // Track site colors for layer settings emission

  // Hover state for map fade effect
  hoveredSiteId: string | null = null;
  hoveredBidItemId: string | null = null;
  hoveredDate: string | null = null;
  hoveredInspectorId: string | null = null;

  // Sticky selection state for multi-select highlighting
  selectedSiteIdsForHighlight: Set<string> = new Set();
  selectedBidItemIdsForHighlight: Set<string> = new Set();
  selectedInspectorIdsForHighlight: Set<string> = new Set();
  selectedDatesForHighlight: Set<string> = new Set();

  // View By state (site, bid_item, day, inspector)
  selectedViewBy: 'site' | 'bid_item' | 'day' | 'inspector' = 'site';

  // Inspectors (Creators) for the current date range's reports
  availableInspectors: Inspector[] = [];
  private inspectorColorMap: { [inspectorId: string]: string } = {};

  // Bid items with geometries in current date range (for View By Bid Item list)
  bidItemsWithGeometries: Array<{id: string, item: string, description: string}> = [];

  // Dates with reports in current date range (for View By Day list)
  datesWithReports: string[] = [];

  // Layer settings tracking - initialized with fullscreen defaults
  // (updated to non-fullscreen defaults if not in fullscreen mode)
  layerSettings = getLayerDefaults('fullscreen');

  // Type filter settings
  selectedTypeFilters: string[] = [];

  // Label state
  labelsEnabled = false;
  labelBy: LabelByOption = 'bid_item_name';
  labelConfig: MapLabelConfig = { enabled: false, labelBy: 'bid_item_name' };

  // Site filter settings
  availableSites: Array<{id: string, name: string}> = [];
  selectedSiteIds: string[] = []; // Empty array means "all sites selected" (default state)

  // Bid item filter settings
  availableBidItems: Array<{id: string, item: string, description: string}> = [];
  selectedBidItemIds: string[] = []; // Empty array means "all selected" (default state)

  // Control form group toggles (Types tab). One toggle per distinct
  // map_display.group among located forms; forms without a group fall under the
  // default group. Group filters are encoded into selectedTypeFilters as
  // `form_group:<id>` tokens so they coexist with bidItem/activity/delivery.
  // A group is shown IFF its token is present (checked) — same gating as the base
  // types: token absent = hidden, default (empty) = all forms hidden.
  readonly FORM_GROUP_PREFIX = 'form_group:';
  readonly DEFAULT_FORM_GROUP_ID = '__default__';
  readonly DEFAULT_FORM_GROUP_LABEL = 'Forms';
  availableFormGroups: Array<{ id: string; label: string; icon: string | null }> = [];

  // Picture source configuration (extensible for future filtering)
  private readonly PICTURE_SOURCES = {
    report_bid_item: { enabled: true, label: 'Bid Items' },
    report_activity: { enabled: true, label: 'Activities' },
    report_activity_code_header: { enabled: true, label: 'Activity Codes' },
    issue: { enabled: true, label: 'Issues' },
    report_material_delivery: { enabled: true, label: 'Material Deliveries' },
    report_internal_test: { enabled: true, label: 'Quality Tests' },
    report_labor: { enabled: true, label: 'Labor' },
    report_equipment: { enabled: true, label: 'Equipment' }
  };

  // Caching and prefetch
  cacheInfo: any = null;
  isLoadingFromAPI = false;
  projectDetails: any = null;
  private lastAlignmentRefreshKey: string | null = null;
  private prefetchRange = {
    start: null as Date,
    end: null as Date
  };

  // Track if single-site geometry has been drawn to prevent flickering during date navigation
  private singleSiteGeometryDrawn = false;

  // Warranty mode state
  isWarrantyClaimsMode = false;
  isPunchlistMode = false;
  selectedPunchlistItem: any = null;
  showPunchlistItemDetail = false;
  /** Last items-for-map response and the popup fields its markers went out without. */
  private punchlistMapItems: Record<string, unknown>[] = [];
  private punchlistPopupFieldsHidden = '';
  private warrantyId: string | null = null;
  showClaimDetail = false;
  selectedClaim: any = null;
  showIssueDetail = false;
  selectedIssue: any = null;
  /** Issues tab: its issues (current filters) as pins, like punchlist mode. */
  isIssuesMode = false;
  /** The for-map query last fetched, so a url change that is not a filter change is a no-op. */
  private issuesMapKey: string | null = null;
  private issuesMapSub: Subscription | null = null;
  /** Fit the map to the pins on the first fetch after entering the tab only. */
  private issuesMapFitPending = false;

  // Test mode — toggled via localStorage.__test_mode__ = 'true' (for E2E video recording)
  get testMode(): boolean {
    try { return localStorage.getItem('__test_mode__') === 'true'; } catch { return false; }
  }

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private mapService: MapService,
    private dailyReportService: DailyReportService,
    private cacheService: DailyReportCacheService,
    private bidService: BidService,
    private actions$: Actions,
    private appHttpService: AppHttpService,
    private projectsService: ProjectsService,
    private stationRangeMapGeometry: StationRangeMapGeometryService,
    private mapSymbolFactory: MapSymbolFactory,
    private issueService: IssueService,
  ) {
    this.projectId = this.route.snapshot.paramMap.get('projectId');

    // Subscribe to query params to detect fullscreen mode and date changes
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        const wasFullScreen = this.isFullScreen;
        this.isFullScreen = 'fs' in qp;

        // Leave the Issues tab's pins first: exiting resets the layers, which
        // must not undo the warranty / punchlist mode entered below.
        const wasIssues = this.isIssuesMode;
        this.isIssuesMode = qp.tab === ISSUES_TAB;
        if (!this.isIssuesMode && wasIssues) {
          this.exitIssuesMode();
        }

        const wasWarranty = this.isWarrantyClaimsMode;
        this.isWarrantyClaimsMode = qp.tab === 'warranty';

        if (this.isWarrantyClaimsMode && !wasWarranty) {
          this.enterWarrantyClaimsMode();
        } else if (!this.isWarrantyClaimsMode && wasWarranty) {
          this.exitWarrantyClaimsMode();
        }

        const wasPunchlist = this.isPunchlistMode;
        this.isPunchlistMode = qp.tab === 'punchlist';
        if (this.isPunchlistMode && !wasPunchlist) {
          this.enterPunchlistMode();
        } else if (!this.isPunchlistMode && wasPunchlist) {
          this.exitPunchlistMode();
        }

        if (this.isIssuesMode && !wasIssues) {
          this.enterIssuesMode();
        }
        if (this.isIssuesMode) {
          this.refreshIssuesMap(qp);
        }

        let dateChanged = false;

        // Watch for date range changes from query params
        if (qp.startDate && moment(qp.startDate).isValid()) {
          const newStart = moment(qp.startDate).toDate();
          if (!this.dateRange.start || this.dateRange.start.getTime() !== newStart.getTime()) {
            this.dateRange.start = newStart;
            dateChanged = true;
          }
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          const newEnd = moment(qp.endDate).toDate();
          if (!this.dateRange.end || this.dateRange.end.getTime() !== newEnd.getTime()) {
            this.dateRange.end = newEnd;
            dateChanged = true;
          }
        }

        if (qp.date && moment(qp.date).isValid() && !qp.startDate && !qp.endDate) {
          const singleDate = moment(qp.date).toDate();
          if (!this.dateRange.start || this.dateRange.start.getTime() !== singleDate.getTime()) {
            this.dateRange.start = singleDate;
            dateChanged = true;
          }
          if (!this.dateRange.end || this.dateRange.end.getTime() !== singleDate.getTime()) {
            this.dateRange.end = singleDate;
            dateChanged = true;
          }
        }

        // In fullscreen mode, if date changed, just re-filter the existing data
        // The API doesn't support date filtering, so we filter client-side
        if (this.isFullScreen && dateChanged && !this.isWarrantyClaimsMode && !this.isIssuesMode && this.sites && this.sites.length > 0) {
          this.filterAndUpdateMap();
        }

        // If we just entered fullscreen and already have data, process it immediately
        if (this.isFullScreen && !wasFullScreen && !this.isWarrantyClaimsMode && !this.isPunchlistMode && !this.isIssuesMode) {
          // Reset layer settings to defaults for fullscreen mode
          this.layerSettings = getLayerDefaults('fullscreen');

          // If we have sites data already, call filterAndUpdateMap
          if (Array.isArray(this.sites) && this.sites.length > 0 && this.allProjectSites.length > 0) {
            this.filterAndUpdateMap();

            // Fetch picture markers on fullscreen enter if picture layer is enabled
            if (this.layerSettings.picture) {
              this.fetchPictureMarkers();
            }
          }
        }

        if (this.isFullScreen && !wasFullScreen && this.isWarrantyClaimsMode && wasWarranty) {
          this.enterWarrantyClaimsMode();
        }

        if (this.isFullScreen && !wasFullScreen && this.isPunchlistMode && wasPunchlist) {
          this.enterPunchlistMode();
        }

        if (this.isFullScreen && !wasFullScreen && this.isIssuesMode && wasIssues) {
          this.enterIssuesMode();
          this.refreshIssuesMap(qp);
        }

        // If we just exited fullscreen, reset layer settings and clear all markers
        if (!this.isFullScreen && wasFullScreen) {
          // Reset layer settings to defaults for non-fullscreen mode FIRST
          // This ensures exitDrilldownMode uses correct settings
          this.layerSettings = getLayerDefaults('nonFullscreen');

          // Exit drilldown mode if active (clear selected item data)
          // This ensures drilldown markers are removed and map returns to default state
          this.mapService.selectedItemData.next(null);

          // Reset type filters to match layer settings
          this.selectedTypeFilters = [];

          // Reset View By to default (site for date-range mode)
          this.selectedViewBy = 'site';

          // Clear sticky selections
          this.selectedSiteIdsForHighlight = new Set();
          this.selectedBidItemIdsForHighlight = new Set();
          this.selectedInspectorIdsForHighlight = new Set();
          this.selectedDatesForHighlight = new Set();

          // Clear all marker data
          this.mapService.dailyReportPictures.next([]);
          this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
          this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
          this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
          this.mapService.dailyReportForms.next({ markers: [], noLocationCount: 0, noLocationItems: [] });

          // Clear geometries and update map (site boundaries will be redrawn)
          this.reportGeometries = [];
          this.updateMapWithGeometries();
        }
      });
  }

  ngOnInit() {
    // Get reference to the map container element for fullscreen
    this.fullscreenElement = this.mapContainer.nativeElement;

    // Clear any stale geometry data from previous projects
    // This prevents geometries from one project persisting when navigating to another
    this.mapService.siteReportGeometries.next(null);
    // ...which also dropped the record mode's layer settings the constructor sent
    // (fullscreen; the header map's site handling is unchanged).
    if (this.isFullScreen && this.isRecordMode) {
      this.pushRecordModeSiteGeometries();
    }

    // A bookmarked full-screen URL opened cold has no project page to fetch the project (sites, alignments): fetch it
    this.store.select(projectDetails).pipe(take(1)).subscribe(details => {
      if (mapNeedsProjectDetails(details, this.projectId, this.route.snapshot.queryParamMap.has('fs'))) {
        this.store.dispatch(FetchProjectDetailsRequest({ payload: mapProjectDetailsPayload(this.projectId) }));
      }
    });

    // Subscribe to project details to get all sites
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data).length !== 0) {
          this.projectDetails = data;
          this.allProjectSites = data.sites || [];

          // The project's punch list field config can land after the item
          // markers; rebuild them if it hides something their popup shows.
          this.refreshPunchlistItemPopupFields();

          // The Issues tab's date range is read in the project's timezone.
          if (this.isIssuesMode) {
            this.refreshIssuesMap(this.route.snapshot.queryParams);
          }

          // projectDetails carries the alignment geometry, which control-form
          // station ranges need to resolve a location. If the alignment arrives
          // AFTER the daily reports were cached (common on a cold/cleared cache),
          // recompute the Types-tab form-group toggles now — otherwise the
          // earlier bulk-subscribe pass computed zero located forms and the
          // group toggle never appears. Self-guards on cachedReports + only
          // overwrites when it finds groups.
          this.refreshAvailableFormGroupsFromCache();

          // Populate available sites for filter dropdown
          if (data.sites && data.sites.length > 0) {
            this.availableSites = data.sites.map(site => ({
              id: site.id,
              name: site.name
            }));
          }

          // Set prefetch range from project start to today
          if (data.start_date) {
            const projectStart = moment(data.start_date || '2020-01-01');
            const today = moment();

            this.prefetchRange = {
              start: projectStart.toDate(),
              end: today.toDate()
            };

            // Prefetch data in background (daily-report layers; not for the record modes)
            if (this.isFullScreen && !this.isRecordMode) {
              this.prefetchDailyReports();
            }
          }

          // Fetch all bid items for the project
          if (data.id) {
            this.store.dispatch(FetchAllBidItemsRequest({
              payload: {
                projectId: data.id,
                qp: {
                  include: [
                    {
                      bid_items: ['fields'],
                    },
                  ],
                },
              }
            }));
          }

          // Record modes in fullscreen: the project's sites can land after the mode was entered.
          if (this.isFullScreen && this.isRecordMode && data.id === this.projectId) {
            this.pushRecordModeSiteGeometries();
          }

          // Update map when project sites are loaded
          // IMPORTANT: Check that the project data matches the current route to avoid stale data
          if (this.isFullScreen && !this.isRecordMode && Array.isArray(this.sites) && this.sites.length > 0 && data.id === this.projectId) {
            this.filterAndUpdateMap();
            this.refreshLocationDerivedMarkerLayers();
          }

          // For single-site projects in non-fullscreen mode, always show the site geometry
          // This ensures the site boundary is visible even without entering fullscreen
          // IMPORTANT: Check that the project data matches the current route to avoid stale data from previous projects
          if (!this.isFullScreen && !this.isWarrantyClaimsMode && data.sites && data.sites.length === 1 && data.id === this.projectId) {
            const singleSite = data.sites[0];

            // Send site geometry if available, otherwise just send empty geometries
            // The sites-location-map will show a marker as fallback
            const geometries = [];
            if (singleSite.geometry) {
              geometries.push({
                ...singleSite.geometry,
                siteId: singleSite.id,
                isSiteGeometry: true
              });
            }

            // Send to map service
            this.mapService.siteReportGeometries.next({
              geometries: geometries,
              sitesWithGeometries: singleSite.geometry ? [singleSite.id] : [],
              siteColorMap: {},
              layerSettings: getLayerDefaults('nonFullscreen')
            });
          }

        }
      });

    // A fullscreen load doesn't render the project-details screen, which is what
    // fetches the project (with its sites' boundaries). The record modes need
    // those sites, so fetch the project when the store doesn't hold it.
    if (this.isFullScreen && this.isRecordMode && this.projectDetails?.id !== this.projectId) {
      this.store.dispatch(FetchProjectDetailsRequest({
        payload: { projectId: this.projectId, qp: { include: PROJECT_DETAILS_SCREEN_INCLUDE } },
      }));
    }

    // Subscribe to all bid items for filter dropdown
    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(bidItems => {
        if (bidItems && bidItems.length > 0) {
          this.availableBidItems = bidItems.map(bidItem => ({
            id: bidItem.id,
            item: bidItem.item,
            description: bidItem.description
          }));
        } else {
          this.availableBidItems = [];
        }
      });

    // Subscribe to site-wise daily reports summary
    this.store.select(siteWiseDailyReportsSummary)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const hadSites = this.sites && this.sites.length > 0;
        this.sites = data;

        // Only update map if this is the FIRST time receiving data in fullscreen mode
        // Subsequent updates will be handled by date/filter changes
        if (this.isFullScreen && !hadSites) {
          if (Array.isArray(this.sites) && this.sites.length > 0) {
            this.filterAndUpdateMap();

            // Fetch picture markers on initial load if picture layer is enabled
            if (this.layerSettings.picture) {
              this.fetchPictureMarkers();
            }
          }
        }
      });

    // Dispatch initial request for site-wise daily reports
    if (this.isFullScreen) {
      this.store.dispatch(FetchSiteWiseDailyReportSummaryRequest({
        payload: {
          projectId: this.projectId,
        },
      }));
    }

    // Warranty: track warranty id from store (skip the initial empty state).
    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        if (info?.list?.length) {
          this.warrantyId = info.list[0].id;
          if (this.isWarrantyClaimsMode) {
            this.fetchWarrantyClaimsForMap();
          }
        }
      });

    // If a warranties fetch returns an empty list while we're in warranty mode,
    // the project has no warranties — bail out and let the map show defaults.
    this.actions$
      .pipe(ofType(FetchWarrantiesSuccess), takeUntil(this.onDestroy))
      .subscribe((action: any) => {
        const list = action?.warrantiesInfo?.list || [];
        if (!list.length && this.isWarrantyClaimsMode) {
          this.warrantyId = null;
          this.exitWarrantyClaimsMode();
          this.isWarrantyClaimsMode = false;
        }
      });

    // Warranty: the map always wants every claim, while the claims table holds a
    // filtered, paginated page in this same store slice. Reading the slice for
    // markers would therefore drop pins whenever the table is filtered, so the
    // map fetches its own full list (see fetchWarrantyClaimsForMap) and only
    // uses the slice as a signal that claims changed.
    this.store.select(warrantyClaimsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.isWarrantyClaimsMode) {
          this.fetchWarrantyClaimsForMap();
        }
      });

    // Warranty: subscribe to claim detail panel state
    this.mapService.showClaimDetail$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(payload => {
        if (payload && payload.claim) {
          this.selectedClaim = payload.claim;
          this.showClaimDetail = true;
          // Close issue detail if open — only one panel at a time.
          this.selectedIssue = null;
          this.showIssueDetail = false;
        } else {
          this.selectedClaim = null;
          this.showClaimDetail = false;
        }
      });

    // Warranty: subscribe to issue detail panel state
    this.mapService.showIssueDetail$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(payload => {
        if (payload && payload.issue) {
          this.selectedIssue = payload.issue;
          this.showIssueDetail = true;
          this.selectedClaim = null;
          this.showClaimDetail = false;
          // An Issues tab pin carries the for-map row only: load the issue.
          if (payload.issue._kind === 'issue' && payload.issue.id) {
            const id = payload.issue.id;
            this.issueService.fetchOne(id)
              .then(full => {
                if (this.selectedIssue?.id === id) {
                  this.selectedIssue = { ...full, _kind: 'issue' };
                }
              })
              .catch(() => { /* keep the pin's fields */ });
          }
        } else {
          this.selectedIssue = null;
          this.showIssueDetail = false;
        }
      });

    // Punchlist: when a punchlist item is selected from the map, render the
    // item detail inline in the floating left panel (mirrors warranty pattern,
    // keeps the map visible).
    this.mapService.showPunchlistItemDetail$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(payload => {
        if (payload?.item) {
          const item = payload.item;
          const punchlistId = item.punchlist_uuid || item.punchlist_id;
          this.selectedPunchlistItem = { ...item, projectId: this.projectId, punchlistId };
          this.showPunchlistItemDetail = true;
        } else {
          this.selectedPunchlistItem = null;
          this.showPunchlistItemDetail = false;
        }
      });

    // Initial enter if landing directly on warranty/claims tab
    if (this.isWarrantyClaimsMode) {
      this.enterWarrantyClaimsMode();
    }

    // Check cache status for debugging
    this.checkCacheStatus();
  }

  /** Issues / Punch list / Claims tab: the map shows that tab's records as pins. */
  private get isRecordMode(): boolean {
    return this.isIssuesMode || this.isPunchlistMode || this.isWarrantyClaimsMode;
  }

  /**
   * Record modes: the map's only geometries are the project's site boundaries
   * (no daily-report items). Fullscreen shows them, like the Daily reports
   * fullscreen does; sites without a boundary fall back to their markers. The
   * header map sends none here (siteBoundary is off there), as before.
   */
  private pushRecordModeSiteGeometries(): void {
    const sites = this.isFullScreen && this.projectDetails?.id === this.projectId ? (this.allProjectSites || []) : [];
    const withGeometry = sites.filter(site => site?.geometry?.type && site.geometry.coordinates);
    this.mapService.siteReportGeometries.next({
      geometries: withGeometry.map(site => ({ ...site.geometry, siteId: site.id, isSiteGeometry: true })),
      sitesWithGeometries: withGeometry.map(site => site.id),
      siteColorMap: {},
      layerSettings: this.layerSettings,
    });
  }

  private enterWarrantyClaimsMode(): void {
    this.layerSettings = {
      siteBoundary: this.isFullScreen, // the header map keeps its own site handling
      picture: false,
      asBuilt: false,
      bidItem: false,
      activity: false,
      delivery: false,
      forms: false,
      warrantyClaim: true,
    };
    this.mapService.layerSettingsUpdated.next({ ...this.layerSettings });

    this.mapService.dailyReportPictures.next([]);
    this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportForms.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportForms.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.reportGeometries = [];
    this.pushRecordModeSiteGeometries();
    this.mapService.warrantyClaimMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });

    this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));
    if (this.warrantyId) {
      this.fetchWarrantyClaimsForMap();
    }
    this.fetchWarrantyIssuesForMap();
  }

  private exitWarrantyClaimsMode(): void {
    this.layerSettings = getLayerDefaults(this.isFullScreen ? 'fullscreen' : 'nonFullscreen');
    this.mapService.layerSettingsUpdated.next({ ...this.layerSettings });
    this.mapService.warrantyClaimMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.showClaimDetail$.next(null);
    this.mapService.showIssueDetail$.next(null);

    if (!this.isFullScreen) {
      this.reportGeometries = [];
      this.updateMapWithGeometries();
    } else if (Array.isArray(this.sites) && this.sites.length > 0) {
      this.filterAndUpdateMap();
    }
  }

  private fetchWarrantyClaimsForMap(): void {
    if (!this.warrantyId) return;
    // Deliberately NOT dispatched through the store: FetchWarrantyClaimsRequest
    // writes warrantyClaimsInfo, which the claims table owns. Dispatching here
    // overwrote the table's filtered page with this unfiltered list of 1000,
    // which made the Status and Created-by filters look like they did nothing.
    this.projectsService.fetchWarrantyClaims({
      projectId: this.projectId,
      warrantyId: this.warrantyId,
      start: 0,
      total: 1000,
      qp: { include: ['pictures'] },
    }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: (resp: any) => {
        const claims = (resp?.data || []).map((c: any) => ({ ...c, ...(c.attributes || {}) }));
        this.pushWarrantyClaimMarkers(claims);
      },
      error: () => { /* markers stay as they are */ },
    });
  }

  private pushWarrantyClaimMarkers(claims: any[]): void {
    const markers: any[] = [];
    const noLocationItems: any[] = [];

    claims.forEach(claim => {
      const lat = parseFloat(claim.latitude);
      const lng = parseFloat(claim.longitude);
      if (!isNaN(lat) && !isNaN(lng) && claim.latitude && claim.longitude) {
        markers.push({
          geometry: { type: 'point', latitude: lat, longitude: lng },
          symbol: 'warranty_claims',
          data: { ...claim, claim_status: claim.status, claim_number: claim.claim_number },
          category: 'warranty_claims',
        });
      } else {
        noLocationItems.push(claim);
      }
    });

    this.mapService.warrantyClaimMarkers$.next({
      markers,
      noLocationCount: noLocationItems.length,
      noLocationItems,
    });
  }

  private fetchWarrantyIssuesForMap(): void {
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/issues`,
    ).subscribe({
      next: (resp: any) => {
        const issues = resp?.data || [];
        this.pushWarrantyIssueMarkers(issues);
      },
      error: () => {
        this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      },
    });
  }

  private pushWarrantyIssueMarkers(issues: any[]): void {
    const markers: any[] = [];
    const noLocationItems: any[] = [];

    issues.forEach(issue => {
      const lat = parseFloat(issue.latitude);
      const lng = parseFloat(issue.longitude);
      if (!isNaN(lat) && !isNaN(lng) && issue.latitude && issue.longitude) {
        markers.push({
          geometry: { type: 'point', latitude: lat, longitude: lng },
          symbol: 'warranty_issues',
          data: { ...issue },
          category: 'warranty_issues',
        });
      } else {
        noLocationItems.push(issue);
      }
    });

    this.mapService.warrantyIssueMarkers$.next({
      markers,
      noLocationCount: noLocationItems.length,
      noLocationItems,
    });
  }

  // ---------------------------------------------------------------------------
  // Punchlist mode: render all items + linked Punchlist-category issues on the map.
  // We push to the warrantyClaimMarkers$ / warrantyIssueMarkers$ channels so the
  // existing marker renderer in sites-location-map picks them up without needing
  // a parallel implementation. The data shape mirrors the warranty markers.
  // ---------------------------------------------------------------------------
  private enterPunchlistMode(): void {
    // Mirror warranty mode's layer settings so the renderer activates the marker
    // layer and hides the other (site/picture/asBuilt/etc.) layers.
    this.layerSettings = {
      siteBoundary: this.isFullScreen, // the header map keeps its own site handling
      picture: false,
      asBuilt: false,
      bidItem: false,
      activity: false,
      delivery: false,
      warrantyClaim: true,
    };
    this.mapService.layerSettingsUpdated.next({ ...this.layerSettings });

    this.mapService.dailyReportPictures.next([]);
    this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.reportGeometries = [];
    this.pushRecordModeSiteGeometries();

    this.mapService.warrantyClaimMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });

    this.fetchPunchlistItemsForMap();
    this.fetchPunchlistIssuesForMap();
  }

  private exitPunchlistMode(): void {
    this.punchlistMapItems = [];
    this.layerSettings = getLayerDefaults(this.isFullScreen ? 'fullscreen' : 'nonFullscreen');
    this.mapService.layerSettingsUpdated.next({ ...this.layerSettings });
    this.mapService.warrantyClaimMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });

    if (!this.isFullScreen) {
      this.reportGeometries = [];
      this.updateMapWithGeometries();
    } else if (Array.isArray(this.sites) && this.sites.length > 0) {
      this.filterAndUpdateMap();
    }
  }

  // ---------------------------------------------------------------------------
  // Issues mode: the Issues tab's issues (its current filters) as pins, on the
  // warrantyIssueMarkers$ channel like punchlist mode. Unlike the punch list /
  // claims tabs, which pin every record, the pins follow the tab's filters so
  // map and list agree; the map fits to them only when the tab opens.
  // ---------------------------------------------------------------------------
  private enterIssuesMode(): void {
    this.layerSettings = {
      siteBoundary: this.isFullScreen, // the header map keeps its own site handling
      picture: false,
      asBuilt: false,
      bidItem: false,
      activity: false,
      delivery: false,
      warrantyClaim: true,
    };
    this.mapService.layerSettingsUpdated.next({ ...this.layerSettings });

    this.mapService.dailyReportPictures.next([]);
    this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.reportGeometries = [];
    this.pushRecordModeSiteGeometries();

    this.mapService.warrantyClaimMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
    this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [], fit: false });

    this.issuesMapKey = null;
    this.issuesMapFitPending = true;
    // Category labels for the no-location list (the Issues tab is not on screen in fullscreen).
    this.store.dispatch(FetchIssueTypesRequest({ payload: { projectId: this.projectId } }));
  }

  private exitIssuesMode(): void {
    this.issuesMapSub?.unsubscribe();
    this.issuesMapSub = null;
    this.issuesMapKey = null;
    this.issuesMapFitPending = false;
    this.projectsService.issuesMapResult.next(null);
    this.exitPunchlistMode();
    this.mapService.showIssueDetail$.next(null);
  }

  /**
   * Fetches the pins for the url's Issues tab filters, unless they are the
   * ones already shown. Waits while the url has no status: the tab is about
   * to write its default there.
   */
  private refreshIssuesMap(qp: Record<string, unknown>): void {
    const state = issuesTabStateFromParams(qp);
    if (!this.projectId || !state.statusSet) {
      return;
    }
    const project = projectForRoute(this.projectDetails, this.projectId) as { timezone?: string } | null;
    const query = issuesMapQuery(this.projectId, qp, project?.timezone);
    const key = JSON.stringify(query);
    if (key === this.issuesMapKey) {
      return;
    }
    this.issuesMapKey = key;
    this.issuesMapSub?.unsubscribe();
    this.issuesMapSub = this.issueService.fetchForMap(query)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: ({ rows, meta }) => {
          if (!this.isIssuesMode) return;
          const markers = issueMapMarkers(rows);
          // Pins: fit to them once (the first time pins come). No pins: zoom to the
          // project's sites / project location ('sites'), and still fit on the first
          // pins that do come for a later filter.
          let fit: boolean | 'sites' = this.issuesMapFitPending && markers.length > 0;
          if (fit) this.issuesMapFitPending = false;
          if (!markers.length) fit = 'sites';
          this.mapService.warrantyIssueMarkers$.next({
            markers,
            noLocationCount: meta?.no_location_count || 0,
            noLocationItems: [],
            fit,
          });
          this.projectsService.issuesMapResult.next({ projectId: this.projectId, key, meta: meta || {} });
        },
        error: () => {
          if (!this.isIssuesMode) return;
          this.projectsService.issuesMapResult.next(null);
          this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [], fit: false });
        },
      });
  }

  private fetchPunchlistItemsForMap(): void {
    this.appHttpService.getService(
      `/v1/punchlist/project/${this.projectId}/items-for-map`,
    ).subscribe({
      next: (resp: any) => this.pushPunchlistItemMarkers(resp?.data || []),
      error: () => this.mapService.warrantyClaimMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] }),
    });
  }

  private fetchPunchlistIssuesForMap(): void {
    this.appHttpService.getService(
      `/v1/punchlist/project/${this.projectId}/issues`,
    ).subscribe({
      next: (resp: any) => this.pushPunchlistIssueMarkers(resp?.data || []),
      error: () => this.mapService.warrantyIssueMarkers$.next({ markers: [], noLocationCount: 0, noLocationItems: [] }),
    });
  }

  /**
   * The item fields the marker popup reads that this project hides through
   * its punchlist_item_config. Nothing counts as hidden until the project in
   * the url is the one in the store.
   */
  private hiddenPunchlistPopupFields(): PunchlistItemFieldName[] {
    const project = projectForRoute(this.projectDetails, this.projectId);
    if (!project) return [];
    const shown = punchlistItemFieldMap(resolvePunchlistItemFields(project));
    return PUNCHLIST_POPUP_FIELDS.filter(name => !shown[name]);
  }

  private refreshPunchlistItemPopupFields(): void {
    if (!this.isPunchlistMode || !this.punchlistMapItems.length) return;
    if (this.hiddenPunchlistPopupFields().join(',') !== this.punchlistPopupFieldsHidden) {
      this.pushPunchlistItemMarkers(this.punchlistMapItems);
    }
  }

  private pushPunchlistItemMarkers(items: any[]): void {
    // The popup renders whatever the marker carries, so a hidden field is
    // left off the marker. The item panel refetches the item and applies the
    // config itself.
    const hiddenFields = this.hiddenPunchlistPopupFields();
    this.punchlistMapItems = items;
    this.punchlistPopupFieldsHidden = hiddenFields.join(',');

    const markers: any[] = [];
    items.forEach((it) => {
      const lat = parseFloat(it.latitude);
      const lng = parseFloat(it.longitude);
      if (!isNaN(lat) && !isNaN(lng) && it.latitude && it.longitude) {
        const data = {
          ...it,
          claim_status: it.status,
          claim_number: it.item_number,
          _kind: 'punchlist',
        };
        hiddenFields.forEach(name => delete data[name]);
        markers.push({
          geometry: { type: 'point', latitude: lat, longitude: lng },
          symbol: 'warranty_claims',
          data,
          category: 'warranty_claims',
        });
      }
    });
    this.mapService.warrantyClaimMarkers$.next({
      markers, noLocationCount: 0, noLocationItems: [],
    });
    this.mapService.punchlistItemMarkers$.next({ markers });
  }

  private pushPunchlistIssueMarkers(issues: any[]): void {
    const markers: any[] = [];
    issues.forEach((issue) => {
      const lat = parseFloat(issue.latitude);
      const lng = parseFloat(issue.longitude);
      if (!isNaN(lat) && !isNaN(lng) && issue.latitude && issue.longitude) {
        markers.push({
          geometry: { type: 'point', latitude: lat, longitude: lng },
          symbol: 'warranty_issues',
          data: { ...issue, _kind: 'punchlist' },
          category: 'warranty_issues',
        });
      }
    });
    this.mapService.warrantyIssueMarkers$.next({
      markers, noLocationCount: 0, noLocationItems: [],
    });
    this.mapService.punchlistIssueMarkers$.next({ markers });
  }

  closeClaimDetail(): void {
    this.mapService.showClaimDetail$.next(null);
  }

  closePunchlistItemDetail(): void {
    this.mapService.showPunchlistItemDetail$.next(null);
  }

  openPunchlistItemInSidenav(): void {
    if (!this.selectedPunchlistItem) return;
    const data = { ...this.selectedPunchlistItem };
    this.mapService.showPunchlistItemDetail$.next(null);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { fs: null, subTab: 'items' },
      queryParamsHandling: 'merge',
    }).then(() => {
      this.projectsService.openRightPanel.next({
        component: PunchlistItemDetailComponent,
        data,
      });
    });
  }

  closeIssueDetail(): void {
    this.mapService.showIssueDetail$.next(null);
  }

  onFullscreenChange(isFullscreen: boolean): void {
    this.isFullScreen = isFullscreen;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    // TODO: Implement search functionality
  }

  onDateRangeChange(range: any): void {
    // Handle both formats from sidenav:
    // - emitDateRangeWithoutNavigation() sends {start, end} (used during play/drag)
    // - updateQueryParams() sends {startDate, endDate} (used for arrow/click navigation)
    this.dateRange = {
      start: range.start || range.startDate,
      end: range.end || range.endDate
    };

    // Call filterAndUpdateMap() to update geometries during drag
    // This will use cached data so it's fast
    // Only call in fullscreen mode - non-fullscreen shows site geometry via projectDetails subscription
    if (this.isFullScreen) {
      this.filterAndUpdateMap();

      // Fetch picture markers with new date range
      if (this.layerSettings.picture) {
        this.fetchPictureMarkers();
      }

      // Fetch bid item, activity, and delivery markers with new date range
      if (this.selectedTypeFilters.includes('bidItem')) {
        this.fetchBidItemMarkers();
      }
      if (this.selectedTypeFilters.includes('activities')) {
        this.fetchActivityMarkers();
      }
      if (this.selectedTypeFilters.includes('delivery')) {
        this.fetchDeliveryMarkers();
      }
      this.fetchFormMarkers();
    }
  }

  onViewModeChange(mode: string): void {
    this.viewMode = mode as any;
    // TODO: Implement view mode switching
  }

  onSiteHover(siteId: string | null): void {
    this.hoveredSiteId = siteId;
  }

  onBidItemHover(bidItemId: string | null): void {
    this.hoveredBidItemId = bidItemId;
  }

  onDateHover(date: string | null): void {
    this.hoveredDate = date;
  }

  onInspectorHover(inspectorId: string | null): void {
    this.hoveredInspectorId = inspectorId;
  }

  // Sticky selection handlers (multi-select)
  onSiteSelectionChange(selectedIds: Set<string>): void {
    this.selectedSiteIdsForHighlight = selectedIds;
  }

  onBidItemSelectionChange(selectedIds: Set<string>): void {
    this.selectedBidItemIdsForHighlight = selectedIds;
  }

  onInspectorSelectionChange(selectedIds: Set<string>): void {
    this.selectedInspectorIdsForHighlight = selectedIds;
  }

  onDateSelectionChange(selectedIds: Set<string>): void {
    this.selectedDatesForHighlight = selectedIds;
  }

  onViewByChange(viewBy: 'site' | 'bid_item' | 'day' | 'inspector'): void {
    this.selectedViewBy = viewBy;
  }

  onLayerChange(layers: any): void {
    // If siteBoundary layer changed, reset flag to force redraw of single-site geometry
    if (this.layerSettings.siteBoundary !== layers.siteBoundary) {
      this.singleSiteGeometryDrawn = false;
    }

    this.layerSettings = { ...layers };

    // Don't re-filter when only layer visibility changes
    // Just update the map service with new layer settings
    // This preserves existing geometries and only changes their visibility
    this.updateMapWithGeometries();

    // Fetch picture markers if picture layer changed
    if (layers.picture !== undefined) {
      this.fetchPictureMarkers();
    }
  }

  onLabelToggle(): void {
    this.labelsEnabled = !this.labelsEnabled;
    this.labelConfig = { enabled: this.labelsEnabled, labelBy: this.labelBy };
  }

  onLabelByChange(labelBy: LabelByOption): void {
    this.labelBy = labelBy;
    this.labelConfig = { enabled: this.labelsEnabled, labelBy: this.labelBy };
  }

  /** Spec §7.3 map filter: the Types tab's `linked_only` token, drawn by sites-location-map. */
  get linkedOnly(): boolean {
    return this.selectedTypeFilters.includes(LINKED_ONLY_FILTER);
  }

  // 'linked_only' is a display filter handled inside sites-location-map (bound through
  // `linkedOnly`); it passes through here untouched and never counts as a type or form group.
  onTypeFilterChange(typeFilters: string[]): void {
    this.selectedTypeFilters = typeFilters;

    // Update layerSettings to sync with type filters (for no-location badge visibility)
    this.layerSettings.bidItem = typeFilters.includes('bidItem');
    this.layerSettings.activity = typeFilters.includes('activities');
    this.layerSettings.delivery = typeFilters.includes('delivery');
    // Forms are gated per group via 'form_group:<group>' tokens — the layer is
    // "on" if any group token is selected (shown iff checked, same as base types).
    this.layerSettings.forms = typeFilters.some(f => f.startsWith(this.FORM_GROUP_PREFIX));

    // Emit layer settings update (without re-emitting geometries)
    this.mapService.layerSettingsUpdated.next({
      ...this.layerSettings,
      asBuilt: this.isFullScreen && this.layerSettings.asBuilt
    });

    // Fetch markers based on selected type filters
    this.fetchBidItemMarkers();
    this.fetchActivityMarkers();
    this.fetchDeliveryMarkers();
    this.fetchFormMarkers();
  }

  private filterAndUpdateMap(): void {
    // Daily-report layers only: the record modes draw their own pins + sites.
    if (this.isRecordMode) {
      return;
    }
    // Clone sites array for filtering
    let filteredSites = JSON.parse(JSON.stringify(this.sites));

    // Filter by selected site IDs (from site filter dropdown)
    // Empty array means "all sites selected" (default state)
    if (this.selectedSiteIds.length > 0) {
      filteredSites = filteredSites.filter(site => {
        return this.selectedSiteIds.includes(site.site_id);
      });
    }

    // Legacy filter by site IDs (keeping for backward compatibility)
    filteredSites = filteredSites.filter(site => {
      if (this.siteIdsToFilter.length) {
        return this.siteIdsToFilter.includes(site.site_id);
      }
      return true;
    });

    // Filter by status and creator
    filteredSites = filteredSites.filter(site => {
      if (this.statusIdsToFilter.length || this.creatorIdsToFilter.length) {
        site.daily_reports = site.daily_reports
          .filter(report => {
            if (this.statusIdsToFilter.length) {
              const status = JSON.parse(JSON.stringify(this.statusIdsToFilter));
              const index = this.statusIdsToFilter.indexOf('submitted-reviewed');
              if (index > -1) {
                status.splice(index, 1);
                status.push(...['submitted', 'reviewed']);
              }
              return status.includes(report.status);
            }
            return true;
          })
          .filter(report => {
            if (this.creatorIdsToFilter.length) {
              return this.creatorIdsToFilter.includes(report.created_by_id);
            }
            return true;
          }) || [];

        return site.daily_reports.length > 0;
      }
      return true;
    });

    // Filter by start date
    if (this.dateRange.start) {
      const startMoment = moment(this.dateRange.start);
      filteredSites = filteredSites.filter(site => {
        site.daily_reports = site?.daily_reports?.filter(o =>
          moment(o.report_date, 'YYYY-MM-DD').isSameOrAfter(startMoment)
        ) || [];
        return site.daily_reports.length > 0;
      });
    }

    // Filter by end date
    if (this.dateRange.end) {
      const endMoment = moment(this.dateRange.end);
      filteredSites = filteredSites.filter(site => {
        site.daily_reports = site?.daily_reports?.filter(o =>
          moment(o.report_date, 'YYYY-MM-DD').isSameOrBefore(endMoment)
        ) || [];
        return site.daily_reports.length > 0;
      });
    }

    // Only keep sites with reports
    filteredSites = filteredSites.filter(o => o?.daily_reports?.length);

    // Sort by last updated
    filteredSites = filteredSites.sort((a, b) => {
      const lastUpdatedA = Math.max(...a.daily_reports?.map(report => new Date(report.updated_at).getTime()));
      const lastUpdatedB = Math.max(...b.daily_reports?.map(report => new Date(report.updated_at).getTime()));
      return lastUpdatedB - lastUpdatedA;
    });

    // Store filtered sites for geometry fetching
    this.filteredSites = filteredSites;

    // Extract unique dates from daily_reports for View By Day
    const uniqueDates = new Set<string>();
    filteredSites.forEach(site => {
      if (site.daily_reports && Array.isArray(site.daily_reports)) {
        site.daily_reports.forEach((report: any) => {
          const reportDate = report.date || report.report_date;
          if (reportDate) {
            const normalizedDate = reportDate.split('T')[0];
            uniqueDates.add(normalizedDate);
          }
        });
      }
    });
    this.datesWithReports = Array.from(uniqueDates).sort();

    // Create enhanced sites with filtering information (same as site-wise-reports)
    const filteredSiteIds = new Set(filteredSites.map(site => site.site_id));

    const enhancedSites = this.allProjectSites.map(projectSite => {
      // Check both projectSite.id and projectSite.site_id to match against filteredSiteIds
      const isFiltered = filteredSiteIds.has(projectSite.id) || filteredSiteIds.has(projectSite.site_id);
      const filteredSiteData = filteredSites.find(site =>
        site.site_id === projectSite.id || site.site_id === projectSite.site_id
      );

      return {
        ...projectSite,
        isFiltered,
        daily_reports: isFiltered ? filteredSiteData?.daily_reports || [] : [],
        site_id: projectSite.id,
        site_name: projectSite.name,
        longitude: projectSite.longitude,
        latitude: projectSite.latitude,
        last_updated_at: filteredSiteData?.last_updated_at || projectSite.updated_at
      };
    }).filter(site => site.longitude && site.latitude);

    // Add filtered sites that are not in allProjectSites
    // This handles cases where the site is in the filtered data but not in project details
    filteredSites.forEach(filteredSite => {
      const existsInEnhanced = enhancedSites.some(site =>
        site.id === filteredSite.site_id || site.site_id === filteredSite.site_id
      );

      if (!existsInEnhanced) {
        enhancedSites.push({
          ...filteredSite,
          isFiltered: true,
          site_name: filteredSite.name,
          longitude: filteredSite.longitude,
          latitude: filteredSite.latitude
        });
      }
    });

    // For single-site projects, pre-populate sitesWithGeometries ONLY if site actually has geometry
    // Check for truthy geometry value AND not empty object/string
    let sitesWithGeometries = [];
    if (this.allProjectSites && this.allProjectSites.length === 1) {
      const site = this.allProjectSites[0];
      const hasGeometry = site.geometry &&
                         site.geometry !== '' &&
                         !(typeof site.geometry === 'object' && Object.keys(site.geometry).length === 0);
      if (hasGeometry) {
        sitesWithGeometries = [site.id];
      }
    }

    // Clear geometries FIRST but preserve PREVIOUS sitesWithGeometries list to prevent marker flash
    // Use currentSitesWithGeometries from last geometry fetch, NOT the new empty array
    this.mapService.siteReportGeometries.next({
      geometries: [],
      sitesWithGeometries: this.currentSitesWithGeometries.length > 0 ? this.currentSitesWithGeometries : sitesWithGeometries,
      layerSettings: {
        ...this.layerSettings,
        asBuilt: this.isFullScreen && this.layerSettings.asBuilt // Only show as-builts in fullscreen
      }
    });

    // Send to map service
    this.mapService.initiateSiteLocationsMap.next(enhancedSites);

    // Fetch and update geometries for filtered reports
    this.fetchReportGeometries();

    // Fetch form markers so the dynamic group toggles populate and located forms
    // render in the multi-day map (forms default ON, gated per group).
    if (this.isFullScreen) {
      this.fetchFormMarkers();
    }
  }

  private fetchReportGeometries(): void {
    const isSingleSite = this.allProjectSites && this.allProjectSites.length === 1;

    // For single-site projects: preserve site geometry to prevent flickering during date navigation
    let singleSiteGeometry = null;
    if (isSingleSite && this.singleSiteGeometryDrawn) {
      // Find and preserve the site geometry
      singleSiteGeometry = this.reportGeometries.find(g => g.type === 'site');
    }

    // Clear existing geometries
    this.reportGeometries = [];

    // Only clear the map if we're NOT preserving single-site geometry
    if (!singleSiteGeometry) {
      this.updateMapWithGeometries();
    }

    // For single-site projects (90% use case), always show site boundary even if no reports
    // Check this BEFORE the filteredSites check, since filteredSites may be empty when no reports exist
    if (isSingleSite) {
      const singleSite = this.allProjectSites[0];
      if (singleSite && singleSite.geometry) {
        if (singleSiteGeometry) {
          // Re-use existing geometry object to avoid redraw
          this.reportGeometries.push(singleSiteGeometry);
        } else {
          // First time drawing site geometry
          this.reportGeometries.push({
            geometry: singleSite.geometry,
            type: 'site',
            reportId: null,
            siteId: singleSite.id || singleSite.site_id,
          });
          this.singleSiteGeometryDrawn = true;
        }
      }
    } else {
      // For multi-site projects: add site geometries from filteredSites
      // We need to get geometries from allProjectSites (which has full site data with geometry)
      // since filteredSites may not have geometry data
      const addedSiteIds = new Set<string>();

      this.filteredSites.forEach(filteredSite => {
        const siteId = filteredSite.site_id || filteredSite.id;
        if (addedSiteIds.has(siteId)) return;

        // Find the full site data in allProjectSites (which has geometry)
        const fullSiteData = this.allProjectSites.find(s =>
          (s.id || s.site_id) === siteId || (s.site_id || s.id) === siteId
        );

        if (fullSiteData && fullSiteData.geometry) {
          this.reportGeometries.push({
            geometry: fullSiteData.geometry,
            type: 'site',
            reportId: null,
            siteId: siteId,
          });
          addedSiteIds.add(siteId);
        }
      });
    }

    // If no filtered sites, we've already handled single-site above
    if (!this.filteredSites || this.filteredSites.length === 0) {
      // Update map with single-site boundary if it was added
      this.updateMapWithGeometries();
      return;
    }

    // Collect all report IDs from filtered sites
    const reportIds = [];
    this.filteredSites.forEach(site => {
      if (site.daily_reports && site.daily_reports.length) {
        site.daily_reports.forEach(report => {
          // The API returns daily_report_id instead of id
          if (report && report.daily_report_id) {
            reportIds.push(report.daily_report_id);
          }
        });
      }
    });

    // If no reports but we have filtered sites, check if single-site (already handled above for all cases)
    if (reportIds.length === 0) {
      this.updateMapWithGeometries();
      return;
    }

    // Use cached bulk fetch instead of individual API calls
    const startDate = moment(this.dateRange.start).format('YYYY-MM-DD');
    const endDate = moment(this.dateRange.end).format('YYYY-MM-DD');

    this.dailyReportService.getBulkDailyReports(this.projectId, startDate, endDate, {
      include: [
        'site',
        'createdBy',  // Include creator info for inspector filtering (camelCase for API)
        {
          bid_items: [
            'map_feature_link',
            'sources',
            {
              bid_item: ['fields']  // Include fields for rollup bid items
            },
            'heading',
            'station',
            'pictures',  // Include pictures for location derivation
            'field_values'  // Needed for rollup formula evaluation + custom field display
          ],
        },
        {
          activities: ['map_feature_link', 'pictures']  // Include activities with pictures
        },
        {
          material_deliveries: [
            'map_feature_link',
            'pictures',
            'tickets',
            'bid_item',
            'material',
            { annotations: ['drawing'] },
            'field_values'
          ]
        },
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
      ],
      useCache: true
    })
      .pipe(takeUntil(this.onDestroy))
      .subscribe(
        (reports: any[]) => {
          // Filter reports to only include those from filtered sites
          const filteredSiteIds = this.filteredSites.map(site => site.site_id || site.id);

          const filteredReports = reports.filter(report => {
            const reportSiteId = report.site_id || report.site?.id;
            return filteredSiteIds.includes(reportSiteId);
          });

          // Cache reports for geometry click lookups
          this.cachedReports = filteredReports;

          // Eagerly (re)publish the form group toggles from the cached reports so
          // the Types tab shows the per-group toggles immediately on (re-)enter,
          // without waiting for fetchFormMarkers' separate async fetch.
          this.refreshAvailableFormGroupsFromCache();

          // Register test helpers (only if __test_mode__ is active in localStorage)
          this.registerTestHelpers();

          // Extract inspectors from filtered reports
          this.extractInspectors(filteredReports);

          // Extract bid item geometries from filtered reports
          // Note: Site geometries are already added from allProjectSites above
          filteredReports.forEach(report => {
            const creatorId = report.created_by_id || report.createdBy?.id || report.created_by?.id;

            // Add bid item geometries
            if (report?.bid_items) {
              report.bid_items.forEach(bidItem => {
                // DEBUG: log geometry presence
                // Extract geometry directly from report_bid_item.geometry (GPS devices)
                if (bidItem.geometry) {
                  BidService.extractGeometries(bidItem.geometry).forEach(extracted => {
                    // Compute quantity for this specific feature from its modified_fields
                    let quantity = 0;
                    if (extracted.gps_feature_index != null) {
                      const feature = bidItem.geometry.features?.[extracted.gps_feature_index];
                      const mode = feature?.properties?.modified_fields?.quantityMode || 'measured';
                      quantity = BidService.calculateGpsFeatureQuantity(feature, mode, bidItem.bid_item);
                    }
                    this.reportGeometries.push({
                      geometry: extracted.geometry,
                      bidItem: bidItem.bid_item,
                      report_bid_item_id: bidItem.id || null,
                      bid_item_id: bidItem.bid_item?.id || null,
                      reportId: report.id,
                      siteId: report.site_id || report.site?.id,
                      report_date: report.date || report.report_date,
                      created_by_id: creatorId,
                      quantity,
                      capture_type: extracted.gps_feature_index != null
                        ? (bidItem.geometry.features?.[extracted.gps_feature_index]?.properties?.source?.meta?.geometry_capture_type ?? null)
                        : null,
                      ...(extracted.gps_feature_index != null ? { gps_feature_index: extracted.gps_feature_index } : {}),
                    });
                  });
                }

                // Extract from quantity sources (existing flow)
                else if (bidItem.sources && bidItem.sources.length) {
                  bidItem.sources.forEach(source => {
                    if (source.source_geometry) {
                      // Prefer modified_quantity (inspector-adjusted) over source_quantity (raw)
                      const srcQty = Number(source.modified_quantity ?? source.source_quantity) || 0;
                      this.reportGeometries.push({
                        geometry: source.source_geometry,
                        bidItem: bidItem.bid_item,
                        report_bid_item_id: bidItem.id || null,
                        bid_item_id: bidItem.bid_item?.id || null,
                        reportId: report.id,
                        siteId: report.site_id || report.site?.id,
                        report_date: report.date || report.report_date,
                        created_by_id: report.created_by_id || report.createdBy?.id || report.created_by?.id,
                        quantity: srcQty,
                      });
                    }
                  });
                }

                // NOTE: station ranges are NOT part of the geometry pipeline —
                // they render on a dedicated, drilldown-only station-range layer
                // managed in sites-location-map.
              });
            }
          });

          // Update map with geometries
          this.updateMapWithGeometries();
        },
        error => {
          // Still update map even if error occurs
          this.updateMapWithGeometries();
        }
      );
  }

  private updateMapWithGeometries(): void {
    // Create site-to-color mapping based on filtered sites order
    // Only apply colors in fullscreen mode (where legend is visible)
    const siteColorMap: { [siteId: string]: string } = {};
    if (this.isFullScreen) {
      this.filteredSites.forEach((site, index) => {
        const siteId = site.site_id || site.id;
        siteColorMap[siteId] = MAP_VIEW_BY_COLORS[index % MAP_VIEW_BY_COLORS.length];
      });
    }

    // Store site color map as class property
    this.siteColorMap = siteColorMap;

    // Send geometry data to the map service
    const uniqueGeometries = [];
    const sitesWithGeometries = new Set<string>();
    const bidItemsMap = new Map<string, {id: string, item: string, description: string}>();

    this.reportGeometries.forEach(item => {
      // Track sites that have geometries
      if (item.type === 'site' && item.siteId) {
        sitesWithGeometries.add(item.siteId);
      }

      // Track bid items that have geometries (for View By Bid Item list)
      if (item.bidItem && item.bidItem.id && !bidItemsMap.has(item.bidItem.id)) {
        bidItemsMap.set(item.bidItem.id, {
          id: item.bidItem.id,
          item: item.bidItem.item || '',
          description: item.bidItem.description || ''
        });
      }

      // Two bid items legitimately share a trace (sawcut / removal / new concrete
      // along one curb), so identity has to be part of the key — otherwise the
      // later item's geometry is dropped and its clicks resolve to the earlier one.
      const exists = uniqueGeometries.some(existing =>
        (existing.report_bid_item_id || null) === (item.report_bid_item_id || null) &&
        JSON.stringify(existing.coordinates) === JSON.stringify(item.geometry.coordinates) &&
        existing.type === item.geometry.type
      );

      if (!exists) {
        uniqueGeometries.push({
          ...item.geometry,
          siteId: item.siteId,
          isSiteGeometry: item.type === 'site',
          report_bid_item_id: item.report_bid_item_id || null, // Pass through report_bid_item_id for filtering
          bid_item_id: item.bid_item_id || null, // Pass through bid_item_id for filtering
          bidItemId: item.bid_item_id || null, // Also include as bidItemId for consistency
          report_activity_id: item.report_activity_id || null,
          report_material_delivery_id: item.report_material_delivery_id || null,
          report_date: item.report_date || null, // Pass through report_date for day view mode
          created_by_id: item.created_by_id || null, // Pass through creator for inspector view
          gps_feature_index: item.gps_feature_index !== undefined ? item.gps_feature_index : null,
          capture_type: item.capture_type || null,
          color: item.geometry.color || siteColorMap[item.siteId] || '#0091FF', // Default to blue if no mapping
          lineStyle: item.geometry.lineStyle || null,
          strokeWidth: item.geometry.strokeWidth || null,
          opacity: item.geometry.opacity ?? item.geometry.lineOpacity ?? null,
          bid_item_name: item.bidItem?.description || item.bidItem?.item || '',
          bid_item_code: item.bidItem?.item || '',
          bid_item_unit: item.bidItem?.unit || '',
          quantity: item.quantity || 0,
          site_name: this.availableSites.find(s => s.id === item.siteId)?.name || ''
        });
      }
    });

    // Update tracking variables
    this.currentSitesWithGeometries = Array.from(sitesWithGeometries);
    this.bidItemsWithGeometries = Array.from(bidItemsMap.values()).sort((a, b) => a.item.localeCompare(b.item));

    // Send geometries and sites with geometries to the map component via the map service
    // Always send all geometries - the map component will control visibility based on layerSettings
    // Note: As-builts should only show in fullscreen mode
    const effectiveLayerSettings = {
      ...this.layerSettings,
      asBuilt: this.isFullScreen && this.layerSettings.asBuilt // Only show as-builts in fullscreen
    };

    this.mapService.siteReportGeometries.next({
      geometries: uniqueGeometries,
      sitesWithGeometries: this.currentSitesWithGeometries,
      siteColorMap: siteColorMap, // Include the color mapping
      layerSettings: effectiveLayerSettings,
      inspectorColorMap: this.inspectorColorMap
    });
  }

  /**
   * Extract unique inspectors (creators) from daily reports
   */
  private extractInspectors(reports: any[]): void {
    const inspectorMap = new Map<string, Inspector>();

    reports.forEach(report => {
      // Check both camelCase (API response) and snake_case versions
      const creator = report.createdBy || report.created_by;
      if (creator && typeof creator === 'object' && creator.id) {
        if (!inspectorMap.has(creator.id)) {
          const index = inspectorMap.size;
          inspectorMap.set(creator.id, {
            id: creator.id,
            name: `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || creator.email || 'Unknown',
            email: creator.email,
            color: MAP_VIEW_BY_COLORS[index % MAP_VIEW_BY_COLORS.length]
          });
        }
      } else if (report.created_by_id) {
        // Fallback if created_by object not populated
        if (!inspectorMap.has(report.created_by_id)) {
          const index = inspectorMap.size;
          inspectorMap.set(report.created_by_id, {
            id: report.created_by_id,
            name: 'Inspector',
            color: MAP_VIEW_BY_COLORS[index % MAP_VIEW_BY_COLORS.length]
          });
        }
      }
    });

    this.availableInspectors = Array.from(inspectorMap.values());

    // Build inspector color map for geometry coloring
    this.inspectorColorMap = {};
    this.availableInspectors.forEach(inspector => {
      this.inspectorColorMap[inspector.id] = inspector.color;
    });
  }

  private fetchPictureMarkers(): void {
    if (!this.layerSettings.picture) {
      // Clear pictures if layer is disabled
      this.mapService.dailyReportPictures.next([]);
      return;
    }

    // Prepare API parameters
    const startDate = this.dateRange.start ? moment(this.dateRange.start).format('YYYY-MM-DD') : null;
    const endDate = this.dateRange.end ? moment(this.dateRange.end).format('YYYY-MM-DD') : null;

    if (!startDate || !endDate) {
      this.mapService.dailyReportPictures.next([]);
      return;
    }

    const siteIds = this.filteredSites.map(site => site.site_id || site.id);
    const enabledSources = Object.keys(this.PICTURE_SOURCES).filter(
      key => this.PICTURE_SOURCES[key].enabled
    );

    // Call new optimized API endpoint with caching
    this.dailyReportService.getPictures(
      this.projectId,
      startDate,
      endDate,
      {
        siteIds: siteIds,
        sources: enabledSources,
        limit: 1000,
        useCache: true // Enable caching for pictures
      }
    )
    .pipe(takeUntil(this.onDestroy))
    .subscribe(
      (response) => {
        // Send to map service for rendering
        this.mapService.dailyReportPictures.next(response.data);
      },
      (error) => {
        this.mapService.dailyReportPictures.next([]);
      }
    );
  }

  private fetchBidItemMarkers(): void {
    const showBidItems = this.selectedTypeFilters.includes('bidItem');

    if (!showBidItems) {
      // Clear bid items if not selected
      this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    // Prepare API parameters
    const startDate = this.dateRange.start ? moment(this.dateRange.start).format('YYYY-MM-DD') : null;
    const endDate = this.dateRange.end ? moment(this.dateRange.end).format('YYYY-MM-DD') : null;

    if (!startDate || !endDate) {
      this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    // Fetch daily reports with bid items (using cached data from IndexedDB)
    // NOTE: Also include activities and material_deliveries so cache has complete data for all types
    this.dailyReportService.getBulkDailyReports(this.projectId, startDate, endDate, {
      include: [
        'site',
        'createdBy',  // Include creator info for inspector filtering (camelCase for API)
        {
          bid_items: [
            'map_feature_link',
            'sources',
            {
              bid_item: ['fields']  // Include fields for rollup bid items
            },
            'heading',
            'station',
            'pictures',
            'field_values'
          ],
        },
        {
          activities: ['map_feature_link', 'pictures']  // Include activities for cache consistency
        },
        {
          material_deliveries: [
            'map_feature_link',
            'pictures',
            'tickets',
            'bid_item',
            'material',
            { annotations: ['drawing'] },
            'field_values'
          ]
        },
        // The daily-report cache is keyed by projectId only (last-write-wins), so
        // every fetch that writes it must carry the full relation superset — incl.
        // control_forms — or it strips them and the forms-on-map group toggle/markers
        // vanish intermittently. See fetchFormMarkers + prefetchReports.
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
      ],
      useCache: true // Use cached data from IndexedDB
    })
    .pipe(takeUntil(this.onDestroy))
    .subscribe(
      (reports: any[]) => {
        // Filter reports to only include those from filtered sites
        const filteredSiteIds = this.filteredSites.map(site => site.site_id || site.id);

        const filteredReports = reports.filter(report => {
          const reportSiteId = report.site_id || report.site?.id;
          return filteredSiteIds.includes(reportSiteId);
        });

        const bidItemMarkers = [];
        // Track unique bid_item.id + report.id combinations for no-location count
        const noLocationUniqueBidItems = new Set<string>();
        // Store actual bid items without location
        const noLocationBidItems = [];

        // Group bid items by report.id + bid_item.id to consolidate markers
        const bidItemGroups = new Map<string, {
          reportId: string;
          siteId: string;
          siteName: string;
          reportDate: string;
          projectId: string;
          bidItem: any; // The bid_item reference (not report_bid_item)
          createdById: string;
          lineItems: any[]; // All report_bid_items for this combination
          locations: { latitude: number; longitude: number }[];
          totalQuantity: number;
        }>();

        // First pass: group all bid items by report + bid_item
        filteredReports.forEach(report => {
          if (!report.bid_items || !report.bid_items.length) {
            return;
          }

          report.bid_items.forEach(bidItem => {
            const location = this.calculateBidItemLocation(bidItem, report);
            const uniqueKey = `${report.id}:${bidItem.bid_item?.id}`;

            if (location) {
              // Has location - add to group
              if (!bidItemGroups.has(uniqueKey)) {
                bidItemGroups.set(uniqueKey, {
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  bidItem: bidItem.bid_item,
                  createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id,
                  lineItems: [],
                  locations: [],
                  totalQuantity: 0
                });
              }

              const group = bidItemGroups.get(uniqueKey);
              group.lineItems.push(bidItem);
              group.locations.push(location);

              // Transform sources from source_fields format to field_values format (API gives source_fields, BidService expects field_values)
              const transformedLineItem = this.transformSourceFieldsToFieldValues(bidItem);

              // Calculate quantity properly using BidService (handles both direct quantity and field-based calculations)
              const lineItemQty = this.bidService.calculateBidItemTotalQuantity(bidItem.bid_item, [transformedLineItem]);
              group.totalQuantity += lineItemQty;
            } else {
              // No location - track unique bid_item.id + report.id combination
              if (!noLocationUniqueBidItems.has(uniqueKey)) {
                noLocationUniqueBidItems.add(uniqueKey);

                // Store the actual bid item data for popup
                noLocationBidItems.push({
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  bidItem: bidItem.bid_item,
                  reportBidItem: bidItem,
                  comment: bidItem.comment,
                  allLineItems: [bidItem] // Include line items for drilldown
                });
              } else {
                // Same bid item already exists in no-location - add this line item to it
                const existingItem = noLocationBidItems.find(item =>
                  item.bidItem?.id === bidItem.bid_item?.id && item.reportId === report.id
                );
                if (existingItem && existingItem.allLineItems) {
                  existingItem.allLineItems.push(bidItem);
                }
              }
            }
          });
        });

        // Second pass: create one marker per group with center point and total quantity
        bidItemGroups.forEach((group, key) => {
          // Calculate center point of all locations for this bid item
          const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
          const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
          const centerPoint = {
            latitude: totalLat / group.locations.length,
            longitude: totalLon / group.locations.length
          };

          // Combine all children (sources/as-builts) from all line items
          const allChildren = group.lineItems.reduce((acc, lineItem) => {
            if (lineItem.children && lineItem.children.length > 0) {
              return [...acc, ...lineItem.children];
            }
            return acc;
          }, []);

          // Combine all sources from all line items
          const allSources = group.lineItems.reduce((acc, lineItem) => {
            if (lineItem.sources && lineItem.sources.length > 0) {
              return [...acc, ...lineItem.sources];
            }
            return acc;
          }, []);

          // Create marker with consolidated data
          const markerData = {
            // Use first line item as base but override key properties
            ...group.lineItems[0],
            quantity: group.totalQuantity, // Sum of all line items
            children: allChildren, // Combined children from all line items
            sources: allSources, // Combined sources from all line items
            reportId: group.reportId,
            siteId: group.siteId,
            siteName: group.siteName,
            reportDate: group.reportDate,
            projectId: group.projectId,
            level: 1,
            bid_item: group.bidItem,
            created_by_id: group.createdById, // Include creator for inspector view hover
            // Include all line items for side panel to use
            allLineItems: group.lineItems,
          };

          bidItemMarkers.push({
            geometry: {
              type: 'point',
              longitude: centerPoint.longitude,
              latitude: centerPoint.latitude,
            },
            symbol: 'bid_items',
            data: markerData,
            category: 'bid_items'
          });
        });

        const noLocationCount = noLocationUniqueBidItems.size;

        // Send to map service for rendering
        this.mapService.dailyReportBidItems.next({
          markers: bidItemMarkers,
          noLocationCount: noLocationCount,
          noLocationItems: noLocationBidItems
        });
      },
      (error) => {
        this.mapService.dailyReportBidItems.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      }
    );
  }

  /**
   * Calculate bid item location based on priority:
   * 1. As-builts (center point of geometries from sources)
   * 2. Lat/long directly on bid item
   * 3. Pictures with locations (center point)
   * 4. null (no location - goes to no-location bucket)
   */
  private calculateBidItemLocation(bidItem: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(bidItem, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    // All location sources below are sanitized against the project sites so bad
    // GPS/EXIF values (far from every site) fall through to the next source (and
    // ultimately to "no location") instead of plotting an outlier in the ocean.
    const references = this.getOutlierReferences();

    // Priority 0: Direct geometry on report_bid_item (GPS devices)
    if (bidItem.geometry) {
      const coordinates = BidService.extractCoordinates(bidItem.geometry);
      if (coordinates.length > 0) {
        const centerPoint = rejectOutlierLocation(this.calculateGeometryCenterPoint(coordinates), references);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    // Priority 1: As-builts (geometries from sources)
    if (bidItem.sources && bidItem.sources.length > 0) {
      const geometriesWithCoords = bidItem.sources
        .filter(source => {
          const hasGeometry = source.source_geometry && source.source_geometry.coordinates;
          return hasGeometry;
        })
        .map(source => source.source_geometry.coordinates);

      if (geometriesWithCoords.length > 0) {
        // Calculate center point of all geometries
        const centerPoint = rejectOutlierLocation(this.calculateGeometryCenterPoint(geometriesWithCoords), references);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    // Priority 2: Lat/long directly on bid item
    if (bidItem.latitude && bidItem.longitude) {
      const direct = rejectOutlierLocation({
        latitude: parseFloat(bidItem.latitude),
        longitude: parseFloat(bidItem.longitude)
      }, references);
      if (direct) {
        return direct;
      }
    }

    // Priority 3: Pictures with locations
    if (bidItem.pictures && bidItem.pictures.length > 0) {
      const picturesWithLocation = bidItem.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        // Calculate center point of all picture locations
        const centerPoint = rejectOutlierLocation(this.calculatePicturesCenterPoint(picturesWithLocation), references);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    // Priority 4: No location (or every source was an outlier)
    return null;
  }

  /**
   * Calculate center point from geometry coordinates
   */
  private calculateGeometryCenterPoint(geometries: any[]): { latitude: number, longitude: number } | null {
    let totalLat = 0;
    let totalLon = 0;
    let count = 0;

    // Recursive function to extract all coordinate pairs from nested arrays
    const extractCoordinates = (coords: any, depth: number = 0): void => {
      if (!Array.isArray(coords)) {
        return;
      }

      // Check if this is a coordinate pair [lon, lat]
      if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        totalLon += coords[0];
        totalLat += coords[1];
        count++;
        return;
      }

      // Otherwise, recurse into nested arrays
      coords.forEach((item, idx) => {
        if (Array.isArray(item)) {
          extractCoordinates(item, depth + 1);
        }
      });
    };

    geometries.forEach((coords, idx) => {
      extractCoordinates(coords, 0);
    });

    if (count === 0) {
      return null;
    }

    const result = {
      latitude: totalLat / count,
      longitude: totalLon / count
    };

    return result;
  }

  /**
   * Calculate center point from picture locations
   */
  private calculatePicturesCenterPoint(pictures: any[]): { latitude: number, longitude: number } {
    const totalLat = pictures.reduce((sum, pic) => sum + parseFloat(pic.latitude), 0);
    const totalLon = pictures.reduce((sum, pic) => sum + parseFloat(pic.longitude), 0);

    return {
      latitude: totalLat / pictures.length,
      longitude: totalLon / pictures.length
    };
  }

  /**
   * Fetch and process activity markers
   */
  private fetchActivityMarkers(): void {
    const showActivities = this.selectedTypeFilters.includes('activities');

    if (!showActivities) {
      // Clear activities if not selected
      this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    // Prepare API parameters
    const startDate = this.dateRange.start ? moment(this.dateRange.start).format('YYYY-MM-DD') : null;
    const endDate = this.dateRange.end ? moment(this.dateRange.end).format('YYYY-MM-DD') : null;

    if (!startDate || !endDate) {
      this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    // Fetch daily reports with activities (using cached data from IndexedDB)
    this.dailyReportService.getBulkDailyReports(this.projectId, startDate, endDate, {
      include: [
        'site',
        'createdBy',  // Include creator info for inspector filtering (camelCase for API)
        {
          bid_items: [
            'map_feature_link',
            'sources',
            {
              bid_item: ['fields']  // Include fields for rollup bid items
            },
            'heading',
            'station',
            'pictures'
          ]
        },
        {
          activities: ['map_feature_link', 'pictures']
        },
        {
          material_deliveries: [
            'map_feature_link',
            'pictures',
            'tickets',
            'bid_item',
            'material',
            { annotations: ['drawing'] },
            'field_values'
          ]
        },
        // Cache is keyed by projectId only (last-write-wins) — keep control_forms
        // in the superset so this writer doesn't strip the forms-on-map data.
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
      ],
      useCache: true // Use cached data from IndexedDB
    })
    .pipe(takeUntil(this.onDestroy))
    .subscribe(
      (reports: any[]) => {
        // Filter reports to only include those from filtered sites
        const filteredSiteIds = this.filteredSites.map(site => site.site_id || site.id);

        const filteredReports = reports.filter(report => {
          const reportSiteId = report.site_id || report.site?.id;
          return filteredSiteIds.includes(reportSiteId);
        });

        const activityMarkers = [];
        const noLocationUniqueActivities = new Set<string>();
        const noLocationActivities = [];

        // Group activities by report.id + activity.id to consolidate markers
        const activityGroups = new Map<string, {
          reportId: string;
          siteId: string;
          siteName: string;
          reportDate: string;
          projectId: string;
          activity: any;
          createdById: string;
          locations: { latitude: number; longitude: number }[];
          pictures: any[];
        }>();

        // First pass: group all activities by report + activity
        filteredReports.forEach(report => {
          if (!report.activities || !report.activities.length) {
            return;
          }

          report.activities.forEach(activity => {
            const location = this.calculateActivityLocation(activity, report);
            const uniqueKey = `${report.id}:${activity.id}`;

            if (location) {
              // Has location - add to group
              if (!activityGroups.has(uniqueKey)) {
                activityGroups.set(uniqueKey, {
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  activity: activity,
                  createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id,
                  locations: [],
                  pictures: []
                });
              }

              const group = activityGroups.get(uniqueKey);
              group.locations.push(location);
              if (activity.pictures && activity.pictures.length > 0) {
                group.pictures.push(...activity.pictures);
              }
            } else {
              // No location - track unique activity
              if (!noLocationUniqueActivities.has(uniqueKey)) {
                noLocationUniqueActivities.add(uniqueKey);

                // Store the actual activity data for popup
                noLocationActivities.push({
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  activity: activity,
                  comment: activity.comment,
                  pictures: activity.pictures || []
                });
              }
            }
          });
        });

        // Second pass: create one marker per group with center point
        activityGroups.forEach((group, key) => {
          // Calculate center point of all locations for this activity
          const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
          const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
          const centerPoint = {
            latitude: totalLat / group.locations.length,
            longitude: totalLon / group.locations.length
          };

          // Create marker with consolidated data
          const markerData = {
            ...group.activity,
            reportId: group.reportId,
            siteId: group.siteId,
            siteName: group.siteName,
            reportDate: group.reportDate,
            projectId: group.projectId,
            level: 1,
            pictures: group.pictures,
            title: group.activity.title || group.activity.name,
            annotations: [], // Activities don't have annotations
            created_by_id: group.createdById, // Include creator for inspector view hover
          };

          activityMarkers.push({
            geometry: {
              type: 'point',
              longitude: centerPoint.longitude,
              latitude: centerPoint.latitude,
            },
            symbol: 'activities',
            data: markerData,
            category: 'activities'
          });
        });



        const noLocationCount = noLocationUniqueActivities.size;

        // Send to map service for rendering
        this.mapService.dailyReportActivities.next({
          markers: activityMarkers,
          noLocationCount: noLocationCount,
          noLocationItems: noLocationActivities
        });
      },
      (error) => {
        this.mapService.dailyReportActivities.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      }
    );
  }

  /**
   * Calculate activity location based on priority:
   * 1. Lat/long directly on activity
   * 2. Pictures with locations (center point)
   * 3. null (no location - goes to no-location bucket)
   */
  private calculateActivityLocation(activity: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(activity, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    const references = this.getOutlierReferences();

    // Priority 1: Lat/long directly on activity
    if (activity.latitude && activity.longitude) {
      const direct = rejectOutlierLocation({
        latitude: parseFloat(activity.latitude),
        longitude: parseFloat(activity.longitude)
      }, references);
      if (direct) {
        return direct;
      }
    }

    // Priority 2: Pictures with locations (center point of all pictures)
    if (activity.pictures && activity.pictures.length > 0) {
      const picturesWithLocation = activity.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        // Calculate center point of all picture locations
        const centerPoint = rejectOutlierLocation(this.calculatePicturesCenterPoint(picturesWithLocation), references);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    // Priority 3: No location (or every source was an outlier)
    return null;
  }

  /**
   * Calculate delivery location based on priority (same as activities):
   * 1. Lat/long directly on delivery
   * 2. Pictures with locations (center point)
   * 3. null (no location - goes to no-location bucket)
   */
  private calculateDeliveryLocation(delivery: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(delivery, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    const references = this.getOutlierReferences();

    // Priority 1: Lat/long directly on delivery
    if (delivery.latitude && delivery.longitude) {
      const direct = rejectOutlierLocation({
        latitude: parseFloat(delivery.latitude),
        longitude: parseFloat(delivery.longitude)
      }, references);
      if (direct) {
        return direct;
      }
    }

    // Priority 2: Pictures with locations (center point of all pictures)
    if (delivery.pictures && delivery.pictures.length > 0) {
      const picturesWithLocation = delivery.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        // Calculate center point of all picture locations
        const centerPoint = rejectOutlierLocation(this.calculatePicturesCenterPoint(picturesWithLocation), references);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    // Priority 3: No location (or every source was an outlier)
    return null;
  }

  private getProjectAlignmentGeometry(report: any = null): any {
    return this.projectDetails?.alignment_geometry
      || this.projectDetails?.alignmentGeometry
      || report?.project?.alignment_geometry
      || report?.project?.alignmentGeometry;
  }

  private refreshLocationDerivedMarkerLayers(): void {
    if (!this.isFullScreen || !this.dateRange.start || !this.dateRange.end || !this.getProjectAlignmentGeometry()) {
      return;
    }

    const startDate = moment(this.dateRange.start).format('YYYY-MM-DD');
    const endDate = moment(this.dateRange.end).format('YYYY-MM-DD');
    const alignmentGeometry = this.getProjectAlignmentGeometry();
    const refreshKey = `${startDate}:${endDate}:${typeof alignmentGeometry === 'string' ? alignmentGeometry.length : JSON.stringify(alignmentGeometry).length}`;
    if (this.lastAlignmentRefreshKey === refreshKey) {
      return;
    }
    this.lastAlignmentRefreshKey = refreshKey;

    if (this.selectedTypeFilters.includes('bidItem')) {
      this.fetchBidItemMarkers();
    }
    if (this.selectedTypeFilters.includes('activities')) {
      this.fetchActivityMarkers();
    }
    if (this.selectedTypeFilters.includes('delivery')) {
      this.fetchDeliveryMarkers();
    }
    this.fetchFormMarkers();
  }

  private getStationRangeLocation(item: any, report: any = null): { latitude: number, longitude: number } | null {
    const stationRange = item?.station_range || item?.stationRange;
    if (!stationRange) return null;
    return this.stationRangeMapGeometry.getStationRangeLocation(
      stationRange,
      this.getProjectAlignmentGeometry(report)
    );
  }

  /**
   * Reference points (every project site, falling back to the project location)
   * used to reject outlier marker locations derived from bad GPS/EXIF. A
   * location is an outlier only when it is far from EVERY site, so projects
   * with sites spread across a wide area still work. Station-range locations are
   * interpolated on the project alignment and are trusted, so they are not
   * sanitized.
   */
  private getOutlierReferences(): GeoPoint[] {
    return computeOutlierReferences(this.allProjectSites, this.projectDetails);
  }

  private fetchDeliveryMarkers(): void {
    const showDeliveries = this.selectedTypeFilters.includes('delivery');

    if (!showDeliveries) {
      // Clear deliveries if not selected
      this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    // Prepare API parameters
    const startDate = this.dateRange.start ? moment(this.dateRange.start).format('YYYY-MM-DD') : null;
    const endDate = this.dateRange.end ? moment(this.dateRange.end).format('YYYY-MM-DD') : null;

    if (!startDate || !endDate) {
      this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    // Fetch daily reports with deliveries (using cached data from IndexedDB)
    this.dailyReportService.getBulkDailyReports(this.projectId, startDate, endDate, {
      include: [
        'site',
        'createdBy',  // Include creator info for inspector filtering (camelCase for API)
        {
          material_deliveries: [
            'map_feature_link',
            'pictures',
            'tickets',
            'bid_item',
            'material',
            { annotations: ['drawing'] },
            'field_values'
          ]
        },
        // Cache is keyed by projectId only (last-write-wins) — keep control_forms
        // in the superset so this writer doesn't strip the forms-on-map data.
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
      ],
      useCache: true // Use cached data from IndexedDB (cache version 6+ includes full material_deliveries)
    })
    .pipe(takeUntil(this.onDestroy))
    .subscribe(
      (reports: any[]) => {
        // Filter reports to only include those from filtered sites
        const filteredSiteIds = this.filteredSites.map(site => site.site_id || site.id);

        const filteredReports = reports.filter(report => {
          const reportSiteId = report.site_id || report.site?.id;
          return filteredSiteIds.includes(reportSiteId);
        });

        const deliveryMarkers = [];
        const noLocationUniqueDeliveries = new Set<string>();
        const noLocationDeliveries = [];

        // Group deliveries by report.id + delivery.id to consolidate markers
        const deliveryGroups = new Map<string, {
          reportId: string;
          siteId: string;
          siteName: string;
          reportDate: string;
          projectId: string;
          delivery: any;
          createdById: string;
          locations: { latitude: number; longitude: number }[];
          pictures: any[];
        }>();

        // First pass: group all deliveries by report + delivery
        filteredReports.forEach(report => {
          if (!report.material_deliveries || !report.material_deliveries.length) {
            return;
          }

          report.material_deliveries.forEach(delivery => {
            const location = this.calculateDeliveryLocation(delivery, report);
            const uniqueKey = `${report.id}:${delivery.id}`;

            if (location) {
              // Has location - add to group
              if (!deliveryGroups.has(uniqueKey)) {
                deliveryGroups.set(uniqueKey, {
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  delivery: delivery,
                  createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id,
                  locations: [],
                  pictures: []
                });
              }

              const group = deliveryGroups.get(uniqueKey);
              group.locations.push(location);
              if (delivery.pictures && delivery.pictures.length > 0) {
                group.pictures.push(...delivery.pictures);
              }
            } else {
              // No location - track unique delivery
              if (!noLocationUniqueDeliveries.has(uniqueKey)) {
                noLocationUniqueDeliveries.add(uniqueKey);

                // Store the actual delivery data for popup - use same structure as map markers
                noLocationDeliveries.push({
                  ...delivery,
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  pictures: delivery.pictures || []
                });
              }
            }
          });
        });

        // Second pass: create one marker per group with center point
        deliveryGroups.forEach((group, key) => {
          // Calculate center point of all locations for this delivery
          const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
          const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
          const centerPoint = {
            latitude: totalLat / group.locations.length,
            longitude: totalLon / group.locations.length
          };

          // Create marker with consolidated data
          const markerData = {
            ...group.delivery,
            reportId: group.reportId,
            siteId: group.siteId,
            siteName: group.siteName,
            reportDate: group.reportDate,
            projectId: group.projectId,
            level: 1,
            pictures: group.pictures,
            title: group.delivery.title || group.delivery.name || 'Material Delivery',
            created_by_id: group.createdById, // Include creator for inspector view hover
          };

          deliveryMarkers.push({
            geometry: {
              type: 'point',
              longitude: centerPoint.longitude,
              latitude: centerPoint.latitude,
            },
            symbol: 'deliveries',
            data: markerData,
            category: 'deliveries'
          });
        });

        const noLocationCount = noLocationUniqueDeliveries.size;

        // Send to map service for rendering
        this.mapService.dailyReportDeliveries.next({
          markers: deliveryMarkers,
          noLocationCount: noLocationCount,
          noLocationItems: noLocationDeliveries
        });
      },
      (error) => {
        this.mapService.dailyReportDeliveries.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      }
    );
  }

  /**
   * Calculate control form location based on priority (same as deliveries):
   * 1. Station range, 2. Lat/long directly on form, 3. Pictures with locations,
   * 4. null (no location).
   */
  private calculateControlFormLocation(form: any, report: any): { latitude: number, longitude: number } | null {
    const stationRangeLocation = this.getStationRangeLocation(form, report);
    if (stationRangeLocation) {
      return stationRangeLocation;
    }

    const references = this.getOutlierReferences();

    if (form.latitude && form.longitude) {
      const direct = rejectOutlierLocation({
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude)
      }, references);
      if (direct) {
        return direct;
      }
    }

    if (form.pictures && form.pictures.length > 0) {
      const picturesWithLocation = form.pictures.filter(pic => pic.latitude && pic.longitude);
      if (picturesWithLocation.length > 0) {
        const centerPoint = rejectOutlierLocation(this.calculatePicturesCenterPoint(picturesWithLocation), references);
        if (centerPoint) {
          return centerPoint;
        }
      }
    }

    return null;
  }

  /**
   * Resolve a control form's group id/label from ui_config.map_display.group.
   * Forms with no group fall under the default group ("Forms").
   */
  private getControlFormGroup(controlForm: any): { id: string; label: string } {
    const group = controlForm?.ui_config?.map_display?.group;
    if (group && `${group}`.trim()) {
      const label = `${group}`.trim();
      return { id: label, label };
    }
    return { id: this.DEFAULT_FORM_GROUP_ID, label: this.DEFAULT_FORM_GROUP_LABEL };
  }

  /**
   * Compute the distinct control-form groups among LOCATED forms in the given
   * reports (one Types-tab toggle per group). Sorted with the default group last.
   * Pure/synchronous so it can run eagerly (no async fetch) on map (re-)enter.
   */
  private computeFormGroups(reports: any[]): Array<{ id: string; label: string; icon: string | null }> {
    const groupsMap = new Map<string, { id: string; label: string; icon: string | null }>();

    (reports || []).forEach(report => {
      (report?.control_forms || []).forEach(form => {
        // Only forms that have a location contribute a group toggle.
        if (!this.calculateControlFormLocation(form, report)) {
          return;
        }
        const group = this.getControlFormGroup(form.control_form);
        if (!groupsMap.has(group.id)) {
          groupsMap.set(group.id, {
            id: group.id,
            label: group.label,
            // Types-tab glyph (grey, standard light style) — NOT the colored map
            // pin. Paired to map_display.icon via the glyph registry.
            icon: this.mapSymbolFactory.resolveControlFormTypeGlyph(form.control_form),
          });
        }
      });
    });

    return Array.from(groupsMap.values()).sort((a, b) => {
      if (a.id === this.DEFAULT_FORM_GROUP_ID) return 1;
      if (b.id === this.DEFAULT_FORM_GROUP_ID) return -1;
      return a.label.localeCompare(b.label);
    });
  }

  /**
   * Eagerly (re)publish form group toggles from the already-cached reports so the
   * Types tab shows the per-group toggles immediately on (re-)enter, without
   * waiting for fetchFormMarkers' async fetch to complete. Only updates when it
   * finds groups, so a stale empty fetch can't blank out an already-populated list.
   */
  private refreshAvailableFormGroupsFromCache(): void {
    if (!this.cachedReports?.length) {
      return;
    }
    const groups = this.computeFormGroups(this.cachedReports);
    if (groups.length) {
      this.availableFormGroups = groups;
    }
  }

  private fetchFormMarkers(): void {
    // Prepare API parameters
    const startDate = this.dateRange.start ? moment(this.dateRange.start).format('YYYY-MM-DD') : null;
    const endDate = this.dateRange.end ? moment(this.dateRange.end).format('YYYY-MM-DD') : null;

    if (!startDate || !endDate) {
      this.availableFormGroups = [];
      this.mapService.dailyReportForms.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      return;
    }

    this.dailyReportService.getBulkDailyReports(this.projectId, startDate, endDate, {
      include: [
        'site',
        'createdBy',
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
      ],
      useCache: true
    })
    .pipe(takeUntil(this.onDestroy))
    .subscribe(
      (reports: any[]) => {
        const filteredSiteIds = this.filteredSites.map(site => site.site_id || site.id);
        const filteredReports = reports.filter(report => {
          const reportSiteId = report.site_id || report.site?.id;
          return filteredSiteIds.includes(reportSiteId);
        });

        const formMarkers = [];
        const noLocationUniqueForms = new Set<string>();
        const noLocationForms = [];

        const formGroups = new Map<string, {
          reportId: string;
          siteId: string;
          siteName: string;
          reportDate: string;
          projectId: string;
          form: any;
          createdById: string;
          locations: { latitude: number; longitude: number }[];
          pictures: any[];
        }>();

        filteredReports.forEach(report => {
          if (!report.control_forms || !report.control_forms.length) {
            return;
          }

          report.control_forms.forEach(form => {
            const location = this.calculateControlFormLocation(form, report);
            const uniqueKey = `${report.id}:${form.id}`;

            if (location) {
              if (!formGroups.has(uniqueKey)) {
                formGroups.set(uniqueKey, {
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  form,
                  createdById: report.created_by_id || report.createdBy?.id || report.created_by?.id,
                  locations: [],
                  pictures: []
                });
              }

              const g = formGroups.get(uniqueKey);
              g.locations.push(location);
              if (form.pictures && form.pictures.length > 0) {
                g.pictures.push(...form.pictures);
              }
            } else {
              if (!noLocationUniqueForms.has(uniqueKey)) {
                noLocationUniqueForms.add(uniqueKey);
                noLocationForms.push({
                  ...form,
                  control_form: form.control_form,
                  reportId: report.id,
                  siteId: report.site_id,
                  siteName: report.site?.name,
                  reportDate: report.date || report.report_date,
                  projectId: report.project_id,
                  pictures: form.pictures || []
                });
              }
            }
          });
        });

        // Publish the distinct groups (one toggle per group) for the Types tab.
        this.availableFormGroups = this.computeFormGroups(filteredReports);

        // Shown IFF checked: a form group renders only when its token is present
        // in selectedTypeFilters. No tokens = nothing shown (same as base types).
        const selectedGroupIds = this.getSelectedFormGroupIds();

        // Build markers, including only forms whose group is selected (checked)
        const filteredFormMarkers = [];
        formGroups.forEach(group => {
          const formGroup = this.getControlFormGroup(group.form.control_form);
          if (!selectedGroupIds.includes(formGroup.id)) {
            return;
          }

          const totalLat = group.locations.reduce((sum, loc) => sum + loc.latitude, 0);
          const totalLon = group.locations.reduce((sum, loc) => sum + loc.longitude, 0);
          const centerPoint = {
            latitude: totalLat / group.locations.length,
            longitude: totalLon / group.locations.length
          };

          const markerData = {
            ...group.form,
            control_form: group.form.control_form,
            reportId: group.reportId,
            siteId: group.siteId,
            siteName: group.siteName,
            reportDate: group.reportDate,
            projectId: group.projectId,
            level: 1,
            pictures: group.pictures,
            title: group.form.control_form?.name,
            form_group: formGroup.id,
            created_by_id: group.createdById,
            _mapItemType: 'forms',
          };

          filteredFormMarkers.push({
            geometry: {
              type: 'point',
              longitude: centerPoint.longitude,
              latitude: centerPoint.latitude,
            },
            symbol: 'forms',
            data: markerData,
            category: 'forms'
          });
        });
        formMarkers.push(...filteredFormMarkers);

        // Gate the "No location" list by the SAME selected-group rule as the
        // markers. Otherwise forms whose group isn't checked — including forms
        // with no map_display (the default "Forms" group, which has no toggle when
        // none of its forms are located) — leak into the no-location list. e.g.
        // On-Site Visitors showing up when only "Labor & Equipment" is selected.
        const filteredNoLocationForms = noLocationForms.filter(form =>
          selectedGroupIds.includes(this.getControlFormGroup(form.control_form).id),
        );

        this.mapService.dailyReportForms.next({
          markers: formMarkers,
          noLocationCount: filteredNoLocationForms.length,
          noLocationItems: filteredNoLocationForms
        });
      },
      (error) => {
        this.mapService.dailyReportForms.next({ markers: [], noLocationCount: 0, noLocationItems: [] });
      }
    );
  }

  /**
   * Extract the selected form-group ids from selectedTypeFilters (the
   * `form_group:<id>` tokens). Empty array = no group checked → no forms shown.
   */
  private getSelectedFormGroupIds(): string[] {
    return this.selectedTypeFilters
      .filter(f => f.startsWith(this.FORM_GROUP_PREFIX))
      .map(f => f.slice(this.FORM_GROUP_PREFIX.length));
  }

  /**
   * Prefetch daily reports for entire project timeline
   * Runs in background, doesn't block UI
   */
  private prefetchDailyReports(): void {
    if (!this.projectId || !this.prefetchRange.start) return;

    const startDate = moment(this.prefetchRange.start).format('YYYY-MM-DD');
    const endDate = moment(this.prefetchRange.end).format('YYYY-MM-DD');

    this.dailyReportService.prefetchReports(
      this.projectId,
      startDate,
      endDate
    );

    // Also prefetch pictures if layer is enabled
    if (this.layerSettings.picture) {
      this.dailyReportService.prefetchPictures(
        this.projectId,
        startDate,
        endDate
      );
    }
  }

  /**
   * Check cache status (for debugging)
   */
  private async checkCacheStatus(): Promise<void> {
    if (this.projectId) {
      this.cacheInfo = await this.cacheService.getCacheInfo(this.projectId);
    }
  }

  /**
   * Force refresh (clear cache and re-fetch)
   */
  async forceRefresh(): Promise<void> {
    if (!this.isFullScreen) {
      return; // Force refresh is only available in fullscreen mode
    }
    this.isLoadingFromAPI = true;
    await this.dailyReportService.refreshReports(this.projectId);
    this.filterAndUpdateMap();
    if (this.layerSettings.picture) {
      this.fetchPictureMarkers();
    }
    this.isLoadingFromAPI = false;
  }

  /**
   * Transform sources from source_fields format (from API) to field_values format (expected by BidService)
   * BidService expects field_values[field.id] = number (not {id, value} object)
   */
  private transformSourceFieldsToFieldValues(lineItem: any): any {
    if (!lineItem.sources || lineItem.sources.length === 0) {
      return lineItem;
    }

    const bidItemFields = lineItem.bid_item?.fields || [];
    const transformedSources = lineItem.sources.map(source => {
      const fieldValues = {};

      bidItemFields.forEach(field => {
        fieldValues[field.id] = null;

        // Map source_fields to field values - store value directly, not as object
        source.source_fields?.forEach(sourceField => {
          for (const key in sourceField) {
            if (field.name === key) {
              fieldValues[field.id] = sourceField[key];  // Just the value!
            }
          }
        });
      });

      return {
        ...source,
        field_values: fieldValues
      };
    });

    return {
      ...lineItem,
      sources: transformedSources
    };
  }

  /**
   * Handle site filter changes from sidenav
   */
  onSiteFilterChange(selectedSiteIds: string[]): void {
    this.selectedSiteIds = selectedSiteIds;

    // Site filter is only available in fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    // Re-filter and update map when site filter changes
    this.filterAndUpdateMap();

    // Re-fetch markers for the filtered sites
    if (this.layerSettings.picture) {
      this.fetchPictureMarkers();
    }
    if (this.selectedTypeFilters.includes('bidItem')) {
      this.fetchBidItemMarkers();
    }
    if (this.selectedTypeFilters.includes('activities')) {
      this.fetchActivityMarkers();
    }
    if (this.selectedTypeFilters.includes('delivery')) {
      this.fetchDeliveryMarkers();
    }
    this.fetchFormMarkers();
  }

  /**
   * Handle bid item filter changes from sidenav
   */
  onBidItemFilterChange(selectedBidItemIds: string[]): void {
    this.selectedBidItemIds = selectedBidItemIds;

    // Apply filter to map via MapService
    this.mapService.selectedBidItemIds.next(selectedBidItemIds);
  }

  onGeometryClick(event: { report_bid_item_id: string; bid_item_id: string; gps_feature_index: number | null; siteId: string; reportId: string }): void {
    if (!event.report_bid_item_id) return;

    // Find the report bid item in cached reports
    let reportBidItem: any = null;
    let report: any = null;

    for (const r of this.cachedReports) {
      if (r.bid_items) {
        const found = r.bid_items.find((bi: any) => bi.id === event.report_bid_item_id);
        if (found) {
          reportBidItem = found;
          report = r;
          break;
        }
      }
    }

    if (!reportBidItem) return;

    // Get site name from filteredSites
    const site = this.filteredSites.find(s => (s.site_id || s.id) === event.siteId);
    const siteName = site?.name || site?.site_name || '';

    // Build marker-like data structure matching what showBidItemDetail expects
    const markerData = {
      ...reportBidItem,
      bid_item: reportBidItem.bid_item,
      siteName,
      siteId: event.siteId || report?.site_id,
      reportId: event.reportId || report?.id,
      reportDate: report?.date || report?.report_date,
      projectId: this.projectId,
      allLineItems: this.getAllLineItemsForBidItem(reportBidItem.bid_item?.id, event.siteId),
    };

    this.mapService.showBidItemDetail.next({
      type: 'bid_items',
      data: markerData,
      projectId: this.projectId,
      siteId: event.siteId,
      reportId: event.reportId || report?.id,
      highlightFeatureIndex: event.gps_feature_index,
      highlightLineItemId: event.report_bid_item_id,
    });
  }

  private getAllLineItemsForBidItem(bidItemId: string, siteId: string): any[] {
    const lineItems: any[] = [];
    this.cachedReports.forEach(report => {
      const reportSiteId = report.site_id || report.site?.id;
      if (reportSiteId !== siteId) return;
      if (report.bid_items) {
        report.bid_items.forEach((bi: any) => {
          if (bi.bid_item?.id === bidItemId) {
            lineItems.push(bi);
          }
        });
      }
    });
    return lineItems;
  }

  /**
   * Test mode: pick the first cached entry and simulate a geometry click on it.
   */
  onTestGeometryClick(): void {
    let firstEntry: any = null;
    let firstReport: any = null;
    for (const r of this.cachedReports) {
      if (r.bid_items?.length) {
        firstEntry = r.bid_items[0];
        firstReport = r;
        break;
      }
    }
    if (!firstEntry) return;
    this.onGeometryClick({
      report_bid_item_id: firstEntry.id,
      bid_item_id: firstEntry.bid_item?.id || '',
      gps_feature_index: 0,
      siteId: firstReport.site_id || '',
      reportId: firstReport.id || '',
    });
  }

  // ---- Test mode helpers (only active when localStorage.__test_mode__ === 'true') ----

  private registerTestHelpers(): void {
    if (!this.testMode) {
      delete (window as any).__mapTestHelpers__;
      return;
    }
    (window as any).__mapTestHelpers__ = {
      /**
       * Simulate a geometry click — opens the focused geometry panel.
       * @param reportBidItemId — the `id` (integer) of a report_bid_item from cachedReports
       * @param featureIndex — index into the geometry FeatureCollection (0 if null)
       */
      triggerGeometryClick: (reportBidItemId: string, featureIndex: number = 0) => {
        this.onGeometryClick({
          report_bid_item_id: reportBidItemId,
          bid_item_id: '',
          gps_feature_index: featureIndex,
          siteId: this.filteredSites?.[0]?.id || this.filteredSites?.[0]?.site_id || '',
          reportId: '',
        });
      },
      /**
       * List all report_bid_items currently cached (visible in the date range).
       * Returns [{id, bid_item_name, bid_item_code, quantity, site_name, report_date}]
       */
      getVisibleEntries: () => {
        const entries: any[] = [];
        this.cachedReports.forEach(report => {
          const siteName = this.filteredSites.find(s => (s.site_id || s.id) === report.site_id)?.name || '';
          if (report.bid_items) {
            report.bid_items.forEach((bi: any) => {
              entries.push({
                id: bi.id,
                uuid: bi.uuid,
                bid_item_name: bi.bid_item?.description || bi.bid_item?.name || '',
                bid_item_code: bi.bid_item?.item || bi.bid_item?.code || '',
                quantity: bi.quantity,
                site_name: siteName,
                report_date: report.date || report.report_date,
                has_geometry: !!bi.geometry,
              });
            });
          }
        });
        return entries;
      },
    };
  }

  ngOnDestroy() {
    delete (window as any).__mapTestHelpers__;
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
